/**
 * A5 — Gruppen-Einstellungen (v1.8 WS-Plattform, D-132). Zwei Tabs:
 *
 *  • „Mitglieder" — verwaltet die Gruppen-Mitgliedschaft (Rechte-Union, D-111);
 *    voll am Backend verkabelt (GET/PUT/DELETE /groups/:id/members). Ein
 *    Gruppen-Mitglied muss zuerst Workspace-Mitglied sein (Sub-Scope, kein
 *    Zugang) — die Auswahl speist sich aus den WS-Mitgliedern.
 *  • „Rechte (Gruppen-ACL)" — Capability-Matrix je Systemlisten-Typ. Hängt am
 *    Zwei-Gate-/Capability-Set-Backend (D-125–129), das noch nicht gebaut ist;
 *    daher als sichtbar deaktivierter „noch nicht verfügbar"-Zustand statt als
 *    Mock. Der Override-Hinweis WS-Admin > Gruppen-Admin bleibt sichtbar.
 */

import { useEffect, useState } from "react";

import { ryfeApi } from "../api";
import { useRyfe } from "../RyfeContext";
import { navigate } from "../router";

import { CapabilityMatrix } from "./CapabilityMatrix";

import "./ryfe.scss";

import type { GroupMember, RyfeGroup as Group, WsMember } from "../api";

type Tab = "members" | "acl";

const MembersTab = ({ ws, group }: { ws: string; group: Group }) => {
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [wsMembers, setWsMembers] = useState<WsMember[]>([]);
  const [pick, setPick] = useState("");
  const [role, setRole] = useState<"member" | "admin">("member");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    ryfeApi
      .listGroupMembers(ws, group.id)
      .then((r) => setMembers(r.members))
      .catch(() => undefined);
  };

  useEffect(() => {
    load();
    ryfeApi
      .listMembers(ws)
      .then((r) => setWsMembers(r.members))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws, group.id]);

  const inGroup = new Set(members.map((m) => m.user_id));
  const candidates = wsMembers.filter((m) => !inGroup.has(m.id));

  const add = async () => {
    if (!pick || busy) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      await ryfeApi.addGroupMember(ws, group.id, pick, role);
      setPick("");
      setRole("member");
      load();
    } catch {
      setError("Hinzufügen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const setMemberRole = async (m: GroupMember, next: "member" | "admin") => {
    try {
      await ryfeApi.addGroupMember(ws, group.id, m.email, next);
      load();
    } catch {
      /* keep the shown role — nothing changed */
    }
  };

  const remove = async (m: GroupMember) => {
    try {
      await ryfeApi.removeGroupMember(ws, group.id, m.user_id);
      load();
    } catch {
      /* leave the row — removal failed */
    }
  };

  return (
    <>
      <div className="ryfe-notice ryfe-notice--warn">
        WS-Admin überschreibt Gruppen-Admin.
      </div>

      <div className="ryfe-table-wrap">
        <table className="ryfe-table">
          <thead>
            <tr>
              <th>Mitglied</th>
              <th>Rolle</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {members.length === 0 && (
              <tr className="ryfe-row">
                <td colSpan={3} className="ryfe-muted">
                  Noch keine Mitglieder.
                </td>
              </tr>
            )}
            {members.map((m) => (
              <tr className="ryfe-row" key={m.user_id}>
                <td>{m.email}</td>
                <td>
                  <select
                    className="ryfe-field-input"
                    value={m.role}
                    onChange={(e) =>
                      setMemberRole(m, e.target.value as "member" | "admin")
                    }
                  >
                    <option value="member">Mitglied</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td>
                  <button
                    className="ryfe-btn--link ryfe-btn--danger"
                    onClick={() => remove(m)}
                  >
                    Entfernen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ryfe-toolbar" style={{ marginTop: "0.6rem" }}>
        <select
          className="ryfe-field-input"
          value={pick}
          data-testid="group-add-pick"
          onChange={(e) => setPick(e.target.value)}
        >
          <option value="">Mitglied hinzufügen …</option>
          {candidates.map((m) => (
            <option key={m.id} value={m.email}>
              {m.email}
            </option>
          ))}
        </select>
        <select
          className="ryfe-field-input"
          value={role}
          onChange={(e) => setRole(e.target.value as "member" | "admin")}
        >
          <option value="member">Mitglied</option>
          <option value="admin">Admin</option>
        </select>
        <button
          className="ryfe-btn ryfe-btn--primary"
          disabled={!pick || busy}
          data-testid="group-add-do"
          onClick={add}
        >
          Hinzufügen
        </button>
        {error && <span className="ryfe-auth__error">{error}</span>}
      </div>
    </>
  );
};

// A5 Rechte-Tab (D-128/D-132) — die Capability-Matrix dieser Gruppe. Sie
// unterscheidet sich von der WS-Rechte-Matrix (A13) nur durch den Scope (eine
// Gruppe). Schreiben ist WS-Admin (Server 403 sonst); Override WS-Admin >
// Gruppen-Admin.
const AclTab = ({ ws, groupId }: { ws: string; groupId: string }) => (
  <>
    <div className="ryfe-notice ryfe-notice--warn">
      WS-Admin überschreibt Gruppen-Admin.
    </div>
    <CapabilityMatrix wsId={ws} groupId={groupId} />
  </>
);

export const RyfeGroupSettings = ({ groupId }: { groupId: string }) => {
  const { workspace, groups, group, setGroup } = useRyfe();
  const [tab, setTab] = useState<Tab>("members");

  const current: Group | null =
    group?.id === groupId
      ? group
      : groups.find((g) => g.id === groupId) || null;

  useEffect(() => {
    if (current && group?.id !== current.id) {
      setGroup(current);
    }
  }, [current, group, setGroup]);

  if (!workspace) {
    return <div className="ryfe-muted">Lädt…</div>;
  }
  if (!current) {
    if (groups.length > 0 && !groups.some((g) => g.id === groupId)) {
      return (
        <div className="ryfe-page">
          <h1 className="ryfe-page__title">Gruppe nicht gefunden</h1>
          <button
            className="ryfe-btn ryfe-btn--primary"
            onClick={() => navigate("/")}
          >
            Zur Startseite
          </button>
        </div>
      );
    }
    return <div className="ryfe-muted">Lädt…</div>;
  }

  return (
    <div className="ryfe-page">
      <h1 className="ryfe-page__title">
        Gruppen-Einstellungen · {current.name}
      </h1>

      <div className="ryfe-tabs">
        <button
          className={`ryfe-tab${tab === "members" ? " ryfe-tab--active" : ""}`}
          onClick={() => setTab("members")}
        >
          Mitglieder
        </button>
        <button
          className={`ryfe-tab${tab === "acl" ? " ryfe-tab--active" : ""}`}
          onClick={() => setTab("acl")}
        >
          Rechte (Gruppen-ACL)
        </button>
      </div>

      {tab === "members" ? (
        <MembersTab ws={workspace.id} group={current} />
      ) : (
        <AclTab ws={workspace.id} groupId={current.id} />
      )}
    </div>
  );
};
